import { useEffect, useState, useMemo } from 'react';
import QRCode from 'qrcode';
import { api } from '../api';
import { LogoMark } from '../components/Logo';

const PRINT_CSS = `
@media print {
  @page { size: A4; margin: 8mm; }
  body { background: #fff !important; }
  .sidebar, .sidebar-overlay, .topbar, .no-print, .toast-container { display: none !important; }
  .layout, .main { display: block !important; margin: 0 !important; padding: 0 !important; }
  .page { padding: 0 !important; max-width: none !important; }
  .qr-sheet { gap: 4mm !important; }
  .qr-label { break-inside: avoid; box-shadow: none !important; }
}`;

function Label({ d, url }) {
  const [src, setSrc] = useState('');
  useEffect(() => { QRCode.toDataURL(url, { margin: 1, width: 240, errorCorrectionLevel: 'M' }).then(setSrc).catch(() => {}); }, [url]);
  return (
    <div className="qr-label" dir="rtl" style={{
      display: 'flex', gap: 8, alignItems: 'center', padding: '3mm', border: '1px solid #222', borderRadius: 6,
      background: '#fff', color: '#111', height: '34mm', overflow: 'hidden', fontFamily: 'Inter, sans-serif',
    }}>
      {src ? <img src={src} alt="QR" style={{ width: '28mm', height: '28mm', flexShrink: 0 }} /> : <div style={{ width: '28mm' }} />}
      <div style={{ minWidth: 0, lineHeight: 1.25 }}>
        <div dir="ltr" style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', color: '#111', fontWeight: 800, fontSize: 13, direction: 'ltr' }}><LogoMark size={13} /><span>Physio<span style={{ color: '#1891C8' }}>Fix</span></span></div>
        <div style={{ fontWeight: 700, fontSize: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{d.name}</div>
        <div dir="ltr" style={{ fontFamily: 'monospace', fontSize: 11, textAlign: 'right' }}>S/N {d.serial}</div>
        {d.client_name && <div style={{ fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.client_name}</div>}
        <div style={{ fontSize: 9, color: '#555', marginTop: 2 }}>امسح الكود للصيانة · repair.physiofix.pro</div>
      </div>
    </div>
  );
}

export default function QrLabels() {
  const [devices, setDevices] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [client, setClient] = useState('');
  const [q, setQ] = useState('');
  const origin = window.location.origin;

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const pre = qs.get('device'), contract = qs.get('contract');
    Promise.all([api.pmLabels(), contract ? api.pmContract(contract) : null]).then(([list, c]) => {
      setDevices(list);
      if (c) { setSel(new Set(c.devices.map(d => d.device_id))); setClient(c.client_name || ''); }
      else setSel(new Set(pre ? [pre] : []));
    }).catch(() => {});
  }, []);

  const clients = useMemo(() => [...new Set(devices.map(d => d.client_name).filter(Boolean))].sort(), [devices]);
  const shown = devices.filter(d => (!client || d.client_name === client) &&
    (!q || [d.name, d.serial, d.alias].some(v => v && v.toLowerCase().includes(q.toLowerCase()))));
  const chosen = devices.filter(d => sel.has(d.id) && d.qr_token);
  const toggle = id => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="page" style={{ maxWidth: 'none' }}>
      <style>{PRINT_CSS}</style>
      <div className="no-print" dir="rtl">
        <div className="page-header">
          <h1 className="page-title" dir="ltr">QR LABELS <span>_</span></h1>
          <button className="btn btn-primary" disabled={!chosen.length} onClick={() => window.print()}>🖨 اطبع {chosen.length} ملصق</button>
        </div>
        <div className="flex gap-8 mb-8" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="input" style={{ maxWidth: 220 }} placeholder="🔍 بحث" value={q} onChange={e => setQ(e.target.value)} />
          <select className="select" style={{ width: 'auto' }} value={client} onChange={e => setClient(e.target.value)}>
            <option value="">كل العملاء</option>
            {clients.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={() => setSel(s => new Set([...s, ...shown.map(d => d.id)]))}>تحديد الظاهر</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setSel(new Set())}>إلغاء التحديد</button>
        </div>
        <div className="card mb-16" style={{ padding: 8, maxHeight: 220, overflowY: 'auto' }}>
          {shown.map(d => (
            <label key={d.id} className="flex items-center gap-8 text-sm" style={{ padding: '4px 6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={sel.has(d.id)} onChange={() => toggle(d.id)} />
              <span className="fw-500">{d.name}</span><span className="mono text-xs text-dim">{d.serial}</span>
              <span className="text-xs text-muted">{d.client_name || ''}</span>
            </label>
          ))}
          {shown.length === 0 && <div className="empty text-sm">مفيش أجهزة</div>}
        </div>
        <div className="text-xs text-dim mb-8">كل ملصق بيفتح صفحة الجهاز، ومحدش يقدر يدخلها غير بيوزر وباسورد صح. اطبع على ورق ستيكر A4.</div>
      </div>

      <div className="qr-sheet" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, maxWidth: '194mm' }}>
        {chosen.map(d => <Label key={d.id} d={d} url={`${origin}/d/${d.qr_token}`} />)}
      </div>
    </div>
  );
}
